import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';
import { listAdminEventsByDate } from '../../api/taskEvents';

type EventRow = Record<string, unknown>;
type FetchOptions = {
  startDate?: string; endDate?: string; isInitial?: boolean; sortOrderOverride?: string;
  assignedOnly?: boolean; ignoreSearchFilters?: boolean; fetchLimitOverride?: number; preservePage?: boolean;
};
type Options = {
  canViewCastingProgress: boolean;
  castingSummaryMap: Record<string, unknown>;
  castingSummaryPendingRef: MutableRefObject<Set<string>>;
  filterAssignedOnly: boolean; filterEventName: string;
  filterGenres: unknown[]; filterPrefectures: unknown[]; filterVenues: unknown[]; filterArtists: unknown[];
  filterLocally: (events: EventRow[], sort?: string, options?: { assignedOnly: boolean }) => EventRow[];
  itemsPerPage: number;
  loadCastingSummaries: (ids: string[]) => unknown;
  normalizeSelectionArray: (values: unknown[]) => string[];
  lastFetchLimitRef: MutableRefObject<number>;
  serverMayHaveMoreRef: MutableRefObject<boolean>;
  restoringRef: MutableRefObject<boolean>;
  setIsLoading: (value: boolean) => void;
  setAllFetchedEvents: (events: EventRow[]) => void;
  setEvents: (events: EventRow[]) => void;
  setCurrentPage: (page: number) => void;
  toastError: (message: string) => void;
};
const FETCH_LIMIT_MULTIPLIER = 10;

export default function useEventListFetch({
  canViewCastingProgress, castingSummaryMap, castingSummaryPendingRef, filterAssignedOnly,
  filterEventName, filterGenres, filterPrefectures, filterVenues,
  filterArtists, filterLocally, itemsPerPage, loadCastingSummaries,
  normalizeSelectionArray, lastFetchLimitRef, serverMayHaveMoreRef, restoringRef,
  setIsLoading, setAllFetchedEvents, setEvents, setCurrentPage,
  toastError,
}: Options) {
  const requestSequence = useRef(0);
  useEffect(() => () => { requestSequence.current += 1; }, []);
  // 期間検索（常にサーバー再取得）
  return useCallback(async ({
    startDate,
    endDate,
    isInitial = false,
    sortOrderOverride,
    assignedOnly,
    ignoreSearchFilters = false,
    fetchLimitOverride,
    preservePage = false,
  }: FetchOptions = {}) => {
    const request = ++requestSequence.current;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate)   params.append('endDate', endDate);
      const assignedFlag = typeof assignedOnly === 'boolean' ? assignedOnly : filterAssignedOnly;
      if (assignedFlag) params.append('assignedOnly', 'true');
      if (!ignoreSearchFilters && filterEventName.trim()) params.append('keyword', filterEventName.trim());
      if (!ignoreSearchFilters && filterGenres.length) params.append('genreValues', normalizeSelectionArray(filterGenres).join(','));
      if (!ignoreSearchFilters && filterPrefectures.length) params.append('prefectures', normalizeSelectionArray(filterPrefectures).join(','));
      if (!ignoreSearchFilters && filterVenues.length) params.append('venueIds', normalizeSelectionArray(filterVenues).join(','));
      if (!ignoreSearchFilters && filterArtists.length) params.append('artistIds', normalizeSelectionArray(filterArtists).join(','));
      const hasServerSearchFilters =
        !ignoreSearchFilters &&
        (Boolean(filterEventName.trim()) ||
          filterGenres.length > 0 ||
          filterPrefectures.length > 0 ||
          filterVenues.length > 0 ||
          filterArtists.length > 0);
      const fetchLimit = fetchLimitOverride || (hasServerSearchFilters
        ? 5000
        : Math.min(itemsPerPage * FETCH_LIMIT_MULTIPLIER, 1000));
      params.append('limit', String(fetchLimit));

      const data = {
        events: await listAdminEventsByDate(Object.fromEntries(params.entries())),
      };
      if (request !== requestSequence.current) return;
      lastFetchLimitRef.current = fetchLimit;
      serverMayHaveMoreRef.current = Array.isArray(data.events) && data.events.length >= fetchLimit;

      // サーバー結果をベースに表示用の並びだけローカルで整える
      setAllFetchedEvents(data.events);
      const filtered = ignoreSearchFilters
        ? (data.events || [])
        : filterLocally(data.events, sortOrderOverride, { assignedOnly: assignedFlag });
      setEvents(filtered);
      const eventIds = (data.events || [])
        .map((event) => event?.event_uuid)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);
      if (canViewCastingProgress) {
        const unknownIds = eventIds.filter(
          (id) =>
            !Object.prototype.hasOwnProperty.call(castingSummaryMap, id) &&
            !castingSummaryPendingRef.current.has(id)
        );
        if (unknownIds.length) {
          loadCastingSummaries(unknownIds);
        }
      }

      if (!preservePage && (!restoringRef.current || !isInitial)) {
        setCurrentPage(1);
      }
    } catch (error) {
      if (request !== requestSequence.current) return;
      console.error('Error fetching events:', error);
      toastError('イベント一覧の取得に失敗しました');
    } finally {
      if (request === requestSequence.current) setIsLoading(false);
    }
  }, [
    canViewCastingProgress,
    castingSummaryMap,
    filterAssignedOnly,
    filterEventName,
    filterGenres,
    filterLocally,
    filterArtists,
    filterPrefectures,
    filterVenues,
    itemsPerPage,
    loadCastingSummaries,
    toastError, normalizeSelectionArray, castingSummaryPendingRef, lastFetchLimitRef,
    serverMayHaveMoreRef, restoringRef, setIsLoading, setAllFetchedEvents, setEvents, setCurrentPage,
  ]);

}
